import { z } from "zod";

/**
 * The end user's public name (boxed parity: `usernameSchema` in boxed-frontend's form schemas,
 * `validateDisplayName` on its API): 3–20 letters, digits or underscores, unique per environment
 * regardless of case. `null` on the row until the shopper chooses one.
 */
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 20;
export const USERNAME_PATTERN = /^[A-Za-z0-9_]+$/;

/**
 * Names no end user may take, compared as `usernameKey`: the labels the storefront gives a seat
 * that is not another player ("You", "Bot"), the fallback's word ("Player"), and words that read
 * as the store's own staff.
 */
export const RESERVED_USERNAMES = [
  "you",
  "bot",
  "bots",
  "player",
  "players",
  "admin",
  "administrator",
  "moderator",
  "mod",
  "staff",
  "support",
  "system",
  "official",
] as const;

export const USERNAME_ISSUES = ["too_short", "too_long", "invalid_characters", "reserved"] as const;
export type UsernameIssue = (typeof USERNAME_ISSUES)[number];

/** One sentence per issue, the same on the server's 400 and in the storefront's form. */
export const USERNAME_ISSUE_MESSAGE: Record<UsernameIssue, string> = {
  too_short: `Usernames are at least ${USERNAME_MIN_LENGTH} characters.`,
  too_long: `Usernames are at most ${USERNAME_MAX_LENGTH} characters.`,
  invalid_characters: "Usernames use letters, numbers and underscores only.",
  reserved: "That username is reserved.",
};

/** The uniqueness key: two names with the same key are the same name (`lower(username)` in SQL). */
export function usernameKey(username: string): string {
  return username.toLowerCase();
}

/** What is wrong with a candidate username, or null. Checked in this order; the first hit wins. */
export function usernameIssue(candidate: string): UsernameIssue | null {
  if (candidate.length < USERNAME_MIN_LENGTH) return "too_short";
  if (candidate.length > USERNAME_MAX_LENGTH) return "too_long";
  if (!USERNAME_PATTERN.test(candidate)) return "invalid_characters";
  if ((RESERVED_USERNAMES as readonly string[]).includes(usernameKey(candidate))) return "reserved";
  return null;
}

/**
 * A username as the API accepts it. The length and pattern rules are plain zod checks so the
 * OpenAPI document carries them; the reserved list is a refinement with the same message.
 */
export const usernameSchema = z
  .string()
  .min(USERNAME_MIN_LENGTH, USERNAME_ISSUE_MESSAGE.too_short)
  .max(USERNAME_MAX_LENGTH, USERNAME_ISSUE_MESSAGE.too_long)
  .regex(USERNAME_PATTERN, USERNAME_ISSUE_MESSAGE.invalid_characters)
  .refine((value) => usernameIssue(value) !== "reserved", USERNAME_ISSUE_MESSAGE.reserved);

/** PUT /v1/me/username. */
export const updateMeUsernameRequestSchema = z.object({ username: usernameSchema });
export type UpdateMeUsernameRequest = z.infer<typeof updateMeUsernameRequestSchema>;

/**
 * How other shoppers see a user, on a battle seat, their profile and the forge boxes they built:
 * the username once they have chosen one, else `Player XXXX` from the tail of their ULID, stable
 * per user and unguessable enough for a lobby. Never an external id or an email (a shopper's
 * `external_id` is `shopper_<id>` and their email sits in metadata); the SPA shows "You" for the
 * viewer's own seat.
 */
export function endUserDisplayName(endUserId: string, username: string | null): string {
  return username ?? `Player ${endUserId.slice(-4).toUpperCase()}`;
}
